import moment from 'moment-timezone'
import { buildSeriesDataForSinglePoint, convertToMappingChartDataPoint, groupByDayForMCDataPoint } from './utils'
import { INHALER_USAGE_ICONS, INHALER_USAGES_GRID_TIMESTAMP, Z_INDEX } from '../constants'
import * as R from 'ramda'
import {
  DataPointWithInhalerInfo,
  MappingChartDataPoint,
} from '@/components/patient-stats/graphs/mapping-chart-options/types'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { InhalerTypes } from '@/jsonapi/CommonTypes'
import { removeAbbr } from '@/utils/time'
import { withSingularOrPluralLabel } from '@/utils/labels'

function makeInhalerUsageLabel(name, value) {
  return `${name} ${withSingularOrPluralLabel(value, 'puff', 'puffs')}`
}
function buildTooltip(timezone: string) {
  return {
    show: true,
    formatter: params => {
      const data = params.value[params.value.length - 1]
      const dateLabel = moment(data.rawStartTimestamp)
        .tz(timezone)
        .format(removeAbbr(timezone, CHART_DATE_FORMAT.TIME_ABBR))
      if (data.items) {
        return `${dateLabel}<br>${data.items
          .map(item => makeInhalerUsageLabel(item.rawData.name, data.rawData.value))
          .join(', ')} `
      } else {
        return `${dateLabel}<br>${makeInhalerUsageLabel(data.rawData.name, data.rawData.value)} `
      }
    },
  }
}

function buildOption(usages, icon, options, timezone) {
  const dataByDay = groupByDayForMCDataPoint(usages)
  return {
    ...options,
    ...icon,
    tooltip: buildTooltip(timezone),
    type: 'scatter',
    data: buildSeriesDataForSinglePoint(dataByDay),
    xAxisIndex: 2,
  }
}

function compare<T>(a: MappingChartDataPoint<T>, b: MappingChartDataPoint<T>) {
  return a.shiftedTimestampStartOfDay === b.shiftedTimestampStartOfDay && a.gridIndex === b.gridIndex
}

function innerJoinWithData<T, U>(fn: (a: T, b: U) => boolean, arrA: readonly T[], arrB: readonly U[]) {
  return arrA
    .map(itemA => {
      const itemB = arrB.find(b => fn(itemA, b))
      if (itemB) {
        return {
          ...itemA,
          items: [itemA, itemB],
        }
      }
    })
    .filter(Boolean)
}

export function buildInhalerUsagesSeries({ timezone, inhalerUsages, options }) {
  const dataPoints: DataPointWithInhalerInfo[] = inhalerUsages
    .map(inhalerUsage => {
      return inhalerUsage.dataPoints.map(point => ({
        ...point,
        name: inhalerUsage.name,
        type: inhalerUsage.type,
      }))
    })
    .flat(Infinity)

  const mappingChartDataPoint: Array<MappingChartDataPoint<DataPointWithInhalerInfo>> = dataPoints.map(
    convertToMappingChartDataPoint(timezone, INHALER_USAGES_GRID_TIMESTAMP)
  )

  const groupByType = R.groupBy(usage => usage.rawData.type, mappingChartDataPoint)
  const maintenanceUsages = groupByType[InhalerTypes.Maintenance]
    ? Object.values(R.groupBy(usage => usage.rawData.name, groupByType[InhalerTypes.Maintenance]))
    : []
  const relieverUsages = groupByType[InhalerTypes.Reliever]
    ? Object.values(R.groupBy(usage => usage.rawData.name, groupByType[InhalerTypes.Reliever]))
    : []

  const combinedDataPointM1R1 = innerJoinWithData(compare, maintenanceUsages[0] || [], relieverUsages[0] || [])
  const combinedDataPointM1R2 = innerJoinWithData(compare, maintenanceUsages[0] || [], relieverUsages[1] || [])
  const combinedDataPointM2R1 = innerJoinWithData(compare, maintenanceUsages[0] || [], relieverUsages[1] || [])
  const combinedDataPointM2R2 = innerJoinWithData(compare, maintenanceUsages[0] || [], relieverUsages[1] || [])
  const combinedAllData = [
    ...combinedDataPointM1R1,
    ...combinedDataPointM1R2,
    ...combinedDataPointM2R1,
    ...combinedDataPointM2R2,
  ]

  const maintenanceInhaler1 = R.differenceWith(compare, maintenanceUsages[0] || [], combinedAllData)
  const maintenanceInhaler2 = R.differenceWith(compare, maintenanceUsages[1] || [], combinedAllData)
  const relieverInhaler1 = R.differenceWith(compare, relieverUsages[0] || [], combinedAllData)
  const relieverInhaler2 = R.differenceWith(compare, relieverUsages[1] || [], combinedAllData)

  const combinedOptions = {
    ...options,
    z: Z_INDEX.INHALER_USAGE_OVERLAP,
  }
  const series = [
    buildOption(
      combinedDataPointM1R1,
      INHALER_USAGE_ICONS.maintenance1OverlapWithReliever[0],
      combinedOptions,
      timezone
    ),
    buildOption(
      combinedDataPointM1R2,
      INHALER_USAGE_ICONS.maintenance1OverlapWithReliever[1],
      combinedOptions,
      timezone
    ),
    buildOption(
      combinedDataPointM2R1,
      INHALER_USAGE_ICONS.maintenance2OverlapWithReliever[0],
      combinedOptions,
      timezone
    ),
    buildOption(
      combinedDataPointM2R2,
      INHALER_USAGE_ICONS.maintenance2OverlapWithReliever[1],
      combinedOptions,
      timezone
    ),
    buildOption(maintenanceInhaler1, INHALER_USAGE_ICONS.maintenance[0], options, timezone),
    buildOption(maintenanceInhaler2, INHALER_USAGE_ICONS.maintenance[1], options, timezone),
    buildOption(relieverInhaler1, INHALER_USAGE_ICONS.reliever[0], options, timezone),
    buildOption(relieverInhaler2, INHALER_USAGE_ICONS.reliever[1], options, timezone),
  ]
  return series
}
